import { lazy, Suspense, useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./styles/classes.scss";
import "./styles/variables.scss";
import "./styles/styles.scss";
import "./App.css";
import { clientRoutes } from "./utils/routes";
import { AuthHelpers } from "./utils/auth.helpers";
import { LocalStorageHelpers } from "./utils/helpers/localstorage.helpers";
import { CommonEnums } from "./enums/common.enums";
import { AgentApi } from "./utils/api/Agent";
import { AgencyApi } from "./utils/api/Agency";
import { agencyApproved, agentApproved } from "./utils/helpers/common";
import AgentAgencyProfile from "./pages/agentProfile/agentAgencyProfile/AgentAgencyProfile";
import AgentAgencyProfileVerifyPhone from "./pages/agentProfile/agentAgencyProfile/AgentAgencyProfileVerifyPhone";
import AgentAgencyLegalInfo from "./pages/agentProfile/agentAgencyProfile/AgentAgencyLegalInfo";
import AgentAgencyBrandGrammar from "./pages/agentProfile/agentAgencyProfile/AgentAgencyBrandGrammar";
import MasterDesignConfigure from "./pages/dashboard/designTemplates/designTemplateSubTypes/MasterDesignConfigure";
import AgentAgencyApprovalWaiting from "./pages/agentProfile/approvalWaiting/AgentAgencyApprovalWaiting";
import AgencyEmployeeList from "./pages/dashboard/AgencyEmployee/list/AgencyEmployeeList";
import CreateAgencyEmployee from "./pages/dashboard/AgencyEmployee/create/CreateAgencyEmployee";
import AgencyEmployeeDetails from "./pages/dashboard/AgencyEmployee/View/AgencyEmployeeDetails";
import AgentEmployeeList from "./pages/dashboard/agentEmployee/list/AgentEmployeeList";
import CreateAgentEmployee from "./pages/dashboard/agentEmployee/create/CreateAgentEmployee";
import AgentEmployeeDetails from "./pages/dashboard/agentEmployee/View/AgentEmployeeDetails";
import AgencyAgentEmployeeList from "./pages/dashboard/agencyAgentEmployee/list/AgencyAgentEmployeeList";
import AgencyAgentEmployeeDetails from "./pages/dashboard/agencyAgentEmployee/View/AgencyAgentEmployeeDetails";
import OpenHomePosts from "./pages/dashboard/accountSettings/openHome/OpenHomePosts";
import AuctionPosts from "./pages/dashboard/accountSettings/auctionPost/AuctionPosts";
import PropertyPostJourneyView from "./pages/dashboard/workspace/propertypost/propertyJourneyView/PropertyPostJourneyView";
import Ledger from "./pages/dashboard/Ledger/Ledger";
import ManageSubscription from "./pages/dashboard/manageSubscription/ManageSubscription";
import ManagePropertySubscription from "./pages/dashboard/manageSubscription/ManagePropertySubscription";
import ManageAgentSubscription from "./pages/dashboard/manageSubscription/ManageAgentSubscription";
import AgencyPaymentHistory from "./pages/dashboard/agencypaymentHistory/AgencyPaymentHistory";
import PaymentHistory from "./pages/dashboard/paymentHistory/PaymentHistory";
import UpdateAgencyAgentInfo from "./pages/agentProfile/agentAgencyProfile/UpdateAgencyAgentInfo";
import Security from "./pages/dashboard/accountSettings/security/Security";

/**
 * Lazy load page components
 */

const ManageInvitesList = lazy(() => import("./pages/dashboard/manageInvite"));
const InvitationDetails = lazy(
  () => import("./pages/dashboard/manageInvite/view"),
);
const Properties = lazy(() => import("./pages/dashboard/properties"));
const PropertieEdit = lazy(
  () => import("./pages/dashboard/properties/edit/EditPropertieForm"),
);
const AgentProfileView = lazy(
  () => import("./pages/dashboard/profile/agentProfile"),
);
const AgencyProfileView = lazy(
  () => import("./pages/dashboard/profile/agencyProfile"),
);

const Workspace = lazy(() => import("./pages/dashboard/workspace"));
const Subscription = lazy(
  () => import("./pages/dashboard/subscriptions/Subscription"),
);

const PropertyPost = lazy(
  () => import("./pages/dashboard/workspace/propertypost/PropertyPost"),
);
const AgentPropertyPost = lazy(
  () =>
    import("./pages/dashboard/workspace/agentPropertyPost/agentPropertyPost"),
);
const WorkspaceAllActivities = lazy(
  () => import("./pages/dashboard/workspace/activities/WorkspaceAllActivities"),
);
const CreateReviewPost = lazy(
  () => import("./pages/dashboard/workspace/reviewpost/CreateReviewPost"),
);
const ScheduleAuctionList = lazy(
  () => import("./pages/dashboard/workspaceShedule/SceduleAuctionList"),
);
const ScheduleOpenHomeList = lazy(
  () => import("./pages/dashboard/workspace/openhome/ScheduleOpenHomeList"),
);
const OnBoardingPostList = lazy(
  () =>
    import("./pages/dashboard/workspace/onboardingpost/list/OnBoardingPostList"),
);
const AgencyScheduleAuctionList = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyAuctionPost/ScheduleAuctionList"),
);
const AgencyScheduleOpenHomeList = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyopenhome/list/ScheduleOpenHomeList"),
);
const AgencyPropertyPostList = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyPropertyPost/list/PropertyPostList"),
);
const AgencyReviewPostList = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyReviewPost/list/ReviewPostList"),
);
const PropertyPostList = lazy(
  () =>
    import("./pages/dashboard/workspace/propertypost/list/PropertyPostList"),
);
const AgentPropertyPostList = lazy(
  () =>
    import("./pages/dashboard/workspace/agentPropertyPost/list/PropertyPostList"),
);
const AnnouncementPostList = lazy(
  () =>
    import("./pages/dashboard/workspace/announcementpost/list/AnnouncementPostList"),
);

const CreateAnnouncementPost = lazy(
  () =>
    import("./pages/dashboard/workspace/announcementpost/CreateAnnouncementPost"),
);

const AnnouncementPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/announcementpost/view/AnnouncementPostView"),
);

const CoAgentPropertyPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/propertypost/view/CoAgentPropertyPostView"),
);

const CoAgentPropertyView = lazy(
  () =>
    import("./pages/dashboard/workspace/agentPropertyPost/view/CoAgentPropertyPostView"),
);

const AnnouncementTypes = lazy(
  () =>
    import("./pages/dashboard/designTemplates/designTemplateTypes/AnnouncementTypes"),
);

const AgencyCreateAnnouncementPost = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyannouncementpost/CreateAnnouncementPost"),
);

const AgencyAnnouncementPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyannouncementpost/view/AnnouncementPostView"),
);

const AgencyAnnouncementPostList = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyannouncementpost/list/AnnouncementPostList"),
);

const FestivalTypes = lazy(
  () =>
    import("./pages/dashboard/designTemplates/designTemplateTypes/FestivalTemplateTypes/FestivalTypes"),
);

const CreateFestivalPost = lazy(
  () => import("./pages/dashboard/workspace/festivalPost/CreateFestivalPost"),
);

const FestivalPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/festivalPost/view/FestivalPostView"),
);

const FestivalPostList = lazy(
  () =>
    import("./pages/dashboard/workspace/festivalPost/list/FestivalPostList"),
);

const AgencyCreateFestivalPost = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyfestivalpost/CreateFestivalPost"),
);

const AgencyFestivalPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyfestivalpost/view/FestivalPostView"),
);

const AgencyFestivalPostList = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyfestivalpost/list/FestivalPostList"),
);

const AgencyOnboardingPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyonboardingpost/view/AgencyOnboardingPostView"),
);

const AgencyOnboardingPostList = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyonboardingpost/list/AgencyOnboardingPostList"),
);

const AgencyCreateOnBoardingPost = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyonboardingpost/AgencyCreateOnBoardingPost"),
);

const PropertyPlanSuccess = lazy(
  () => import("./pages/dashboard/properties/create/PropertyPlanSuccess"),
);

const PropertyPlanFailed = lazy(
  () => import("./pages/dashboard/properties/create/PropertyPlanFailed"),
);

const PropertyPlanUpgrade = lazy(
  () =>
    import("./pages/dashboard/properties/view/overview/PropertyPlanUpgrade"),
);

const PropertyPlanUpgradeFailed = lazy(
  () =>
    import("./pages/dashboard/properties/view/overview/PropertyPlanUpgradeFailed"),
);

const PropertyPostViaPlanSuccess = lazy(
  () =>
    import("./pages/dashboard/workspace/propertypost/PropertyPostViaPlanSuccess"),
);

const AgentPropertyPostViaPlanSuccess = lazy(
  () =>
    import("./pages/dashboard/workspace/agentPropertyPost/AgentPropertyPostViaPlanSuccess"),
);

const OpenHomePostViaPlanSuccess = lazy(
  () =>
    import("./pages/dashboard/workspace/openhome/OpenHomePostViaPlanSuccess"),
);

const AuctionPostViaPlanSuccess = lazy(
  () => import("./pages/dashboard/workspaceShedule/AuctionPostViaPlanSuccess"),
);

const ConfigurePropertyStages = lazy(
  () => import("./pages/dashboard/accountSettings/configurePropertyStages"),
);

const RecapPostSettings = lazy(
  () =>
    import("./pages/dashboard/accountSettings/recapPostSetting/RecapPostSettings"),
);

const AgencyPaymentMethods = lazy(
  () =>
    import("./pages/dashboard/accountSettings/agencyPaymentMethods/AgencyPaymentMethods"),
);

const SocialMediaAccounts = lazy(
  () =>
    import("./pages/dashboard/accountSettings/socialMediaAccounts/SocialMediaAccounts"),
);

const AgentPlanSuccess = lazy(
  () => import("./pages/dashboard/subscriptions/AgentPlanSuccess"),
);

const AgentPlanFailed = lazy(
  () => import("./pages/dashboard/subscriptions/AgentPlanFailed"),
);

// const Ledger = lazy(
//   () => import("./pages/dashboard/accountSettings/Ledger/Ledger"),
// );

const AgencyLegalInfo = lazy(
  () => import("./pages/agencyProfile/AgencyLegalInfo"),
);

const AgencyAdminApprovalWaiting = lazy(
  () =>
    import("./pages/agencyProfile/approvalWaiting/AgencyAdminApprovalWaiting"),
);

const BrandGrammar = lazy(() => import("./pages/agencyProfile/BrandGrammar"));
const ReviewPostList = lazy(
  () => import("./pages/dashboard/workspace/reviewpost/list/ReviewPostList"),
);
const ScheduleOpenHome = lazy(
  () => import("./pages/dashboard/workspace/openhome"),
);
const WorkspaceSheduleConfigure = lazy(
  () =>
    import("./pages/dashboard/WorkspaceSheduleConfigure/WorkspaceSheduleConfigure"),
);
const AgentPropertyPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/agentPropertyPost/view/PropertyPostView"),
);
const WorkspaceSchedule = lazy(
  () => import("./pages/dashboard/workspaceShedule/WorkspaceSchedule"),
);
const CreateOnBoardingPost = lazy(
  () =>
    import("./pages/dashboard/workspace/onboardingpost/CreateOnBoardingPost"),
);
const AgentRequestView = lazy(
  () => import("./pages/dashboard/agentJoiningRequests/view"),
);
const AccountSettings = lazy(() => import("./pages/dashboard/accountSettings"));
const AgentJoiningRequests = lazy(
  () => import("./pages/dashboard/agentJoiningRequests/list"),
);

const PropertieView = lazy(() => import("./pages/dashboard/properties/view"));
const OnboardingPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/onboardingpost/view/OnboardingPostView"),
);
const OpenHomeDetailView = lazy(
  () => import("./pages/dashboard/workspace/openhome/view/OpenHomeDetailView"),
);
const AgencyOpenHomeDetailView = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyopenhome/view/OpenHomeDetailView"),
);
const AgencyPropertyPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyPropertyPost/view/PropertyPostView"),
);
const AgencyScheduleAuctionView = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyAuctionPost/view/ScheduleAuctionView"),
);
const AgencyReviewPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/agencyReviewPost/view/ReviewPostView"),
);
const PropertyPostView = lazy(
  () =>
    import("./pages/dashboard/workspace/propertypost/view/PropertyPostView"),
);
const ReviewPostView = lazy(
  () => import("./pages/dashboard/workspace/reviewpost/view/ReviewPostView"),
);
const ScheduleAuctionView = lazy(
  () => import("./pages/dashboard/workspaceShedule/view/ScheduleAuctionView"),
);
const LoginVerifyEmail = lazy(
  () => import("./pages/agentSignIn/SignInVerifyEmail"),
);
const HomeComponent = lazy(() => import("./pages/home/Home"));
const Register = lazy(() => import("./pages/agentRegister/Register"));
const SignIn = lazy(() => import("./pages/agentSignIn/SignIn"));
const RegiterVerifyEmail = lazy(
  () => import("./pages/agentRegister/RegisterVerifyEmail"),
);
const AgentProfile = lazy(() => import("./pages/agentProfile/AgentProfile"));
const AgentVerifyPhone = lazy(
  () => import("./pages/agentProfile/AgentVerifyPhone"),
);
const AgencyProfile = lazy(() => import("./pages/agencyProfile/AgencyProfile"));

const EditAndViewAgent = lazy(() => import("./pages/dashboard/agents/edit"));
const AgentsList = lazy(() => import("./pages/dashboard/agents"));
const CreateProperty = lazy(
  () => import("./pages/dashboard/properties/create"),
);

const AgentProfileCreateAgencyProfile = lazy(
  () => import("./pages/agentProfile/AgentProfileCreateAgencyProfile"),
);

const AgencyProfileAgentInfo = lazy(
  () => import("./pages/agencyProfile/AgencyProfileAgentInfo"),
);

const AgencyProfileVerifyPhone = lazy(
  () => import("./pages/agencyProfile/AgencyProfileVerifyPhone"),
);

const AgentAddAgencyInfo = lazy(
  () => import("./pages/agentProfile/AgentAddAgencyInfo"),
);

const DesignTemplateTypes = lazy(
  () => import("./pages/dashboard/designTemplates/designTemplateTypes"),
);
const DesignTemplateSubTypes = lazy(
  () => import("./pages/dashboard/designTemplates/designTemplateSubTypes"),
);
const SavePaymentDetails = lazy(
  () => import("./pages/dashboard/test/SavePaymentDetails"),
);
const AgencyApprovalWaiting = lazy(
  () => import("./pages/approvalWaiting/AgencyApprovalWaiting"),
);

/**
 * TODO:
 *
 * Class for the recaptcha container
 * grecaptcha-badge
 *
 *
 */

const unprotectedRoutes = [
  clientRoutes.register,
  clientRoutes.login,
  clientRoutes.agencyApprovalWaiting,
  clientRoutes.adminApprovalWaiting,
  clientRoutes.agentAgencyApprovalWaiting,
  clientRoutes.agentVerifyEmail,
  clientRoutes.agencyVerifyEmail,
];

function App() {
  const navigate = useNavigate();
  const isUserLoggedIn = AuthHelpers.validateAccessToken();
  const location = useLocation();
  const pathname = location.pathname;
  let processRequest = true;

  let failedIterations = [];

  if (!isUserLoggedIn) {
    processRequest = false;

    for (let index = 0; index < unprotectedRoutes.length; index++) {
      const route = unprotectedRoutes[index] as string;

      if (!pathname?.includes(route)) {
        failedIterations.push(true);
      }
    }

    if (failedIterations.length !== unprotectedRoutes.length) {
      processRequest = true;
    }
  }

  if (!processRequest) navigate(clientRoutes.login);

  const getUserDetails = async (params: { user_type: string }) => {
    const { user_type } = params;

    if (user_type === CommonEnums.roles.agent) {
      const res = await AgentApi.GetAgentProfile();

      const success = res.success;
      const userData = res.data?.data;

      if (!success || !userData) navigate(clientRoutes.login);

      LocalStorageHelpers.setAgentData({
        data: userData,
      });
    }

    if (user_type === CommonEnums.roles.agency) {
      const res = await AgencyApi.getAgencyDetails();

      const success = res.success;
      const userData = res.data?.data;

      if (!success || !userData) navigate(clientRoutes.login);

      LocalStorageHelpers.setAgencyData({
        data: userData,
      });
    }
  };

  useEffect(() => {
    const sessionUserType = LocalStorageHelpers.getSessionUserType();

    if (
      pathname.includes("login") ||
      pathname.includes("register") ||
      pathname.includes("agency-approval-waiting") ||
      pathname.includes("admin-approval-waiting")
    )
      return;

    if (!sessionUserType) navigate(clientRoutes.login);
    const agentData = LocalStorageHelpers.getAgentData();

    if (sessionUserType === CommonEnums.roles.agent) {
      if (agentData?.agency_ref) {
        const agencyData = LocalStorageHelpers.getAgencyData();

        const principalAgentId =
          typeof agencyData?.principal_agent_id === "object"
            ? (agencyData?.principal_agent_id as any)?._id ||
              (agencyData?.principal_agent_id as any)?.$oid
            : agencyData?.principal_agent_id;

        const agentId =
          typeof agentData?._id === "object"
            ? (agentData?._id as any)?.$oid || (agentData?._id as any)
            : agentData?._id;

        const isOwnAgency = principalAgentId === agentId;

        if (
          isOwnAgency &&
          agencyData?.status === CommonEnums.status.pending_for_admin_approval
        ) {
          navigate(clientRoutes.agentAgencyApprovalWaiting);
          return;
        }
      }

      if (!agentApproved()) {
        navigate(clientRoutes.agencyApprovalWaiting);
        return;
      }
    }
    if (!agencyApproved()) navigate(clientRoutes.adminApprovalWaiting);

    getUserDetails({
      user_type: sessionUserType as string,
    });

    const userProfileImages = document.querySelectorAll(".user-profile-image");

    userProfileImages.forEach((profileImage) => {
      profileImage.addEventListener("error", (event: any) => {
        event.src = "images/profile-placeholder-image.png";
      });
    });
  }, []);

  return (
    <div className="App">
      <Suspense fallback={<div></div>}>
        <Routes>
          <Route
            path={clientRoutes?.properties}
            element={
              <Suspense fallback={<div></div>}>
                <Properties />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.createProperties}
            element={
              <Suspense fallback={<div></div>}>
                <CreateProperty />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentJoiningRequests}
            element={
              <Suspense fallback={<div></div>}>
                <AgentJoiningRequests />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentRequestsView}
            element={
              <Suspense fallback={<div></div>}>
                <AgentRequestView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agents}
            element={
              <Suspense fallback={<div></div>}>
                <AgentsList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentViewAndEdit}
            element={
              <Suspense fallback={<div></div>}>
                <EditAndViewAgent />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.manageInvites}
            element={
              <Suspense fallback={<div></div>}>
                <ManageInvitesList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.manageInvitesDetails}
            element={
              <Suspense fallback={<div></div>}>
                <InvitationDetails />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentProfileView}
            element={
              <Suspense fallback={<div></div>}>
                <AgentProfileView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agencyProfileView}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyProfileView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.workspace}
            element={
              <Suspense fallback={<div></div>}>
                <Workspace />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.subscription}
            element={
              <Suspense fallback={<div></div>}>
                <Subscription />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.ledger}
            element={
              <Suspense fallback={<div></div>}>
                <Ledger />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.manageSubscriptions}
            element={
              <Suspense fallback={<div></div>}>
                <ManageSubscription />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.propertyPlans}
            element={
              <Suspense fallback={<div></div>}>
                <ManagePropertySubscription />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.agentPlans}
            element={
              <Suspense fallback={<div></div>}>
                <ManageAgentSubscription />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.paymentHistory}
            element={
              <Suspense fallback={<div></div>}>
                {LocalStorageHelpers.getSessionUserType() === "agency" ? (
                  <AgencyPaymentHistory />
                ) : (
                  <PaymentHistory />
                )}
              </Suspense>
            }
          />

          {/* <Route
              path={clientRoutes.manageSubscriptions}
              element={<ManageSubscription />}
            />

            <Route
              path={clientRoutes.propertyPlans}
              element={<ManagePropertySubscription />}
            />
            <Route
              path={clientRoutes.agentPlans}
              element={<ManageAgentSubscription />}
            /> */}

          <Route
            path={clientRoutes?.agencyEmployeeList}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyEmployeeList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.createAgencyEmployeeList}
            element={
              <Suspense fallback={<div></div>}>
                <CreateAgencyEmployee />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.agencyViewEmployee}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyEmployeeDetails />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.agentEmployeeList}
            element={
              <Suspense fallback={<div></div>}>
                <AgentEmployeeList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.createAgentEmployeeList}
            element={
              <Suspense fallback={<div></div>}>
                <CreateAgentEmployee />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.agentViewEmployee}
            element={
              <Suspense fallback={<div></div>}>
                <AgentEmployeeDetails />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.agencyAgentEmployeeList}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyAgentEmployeeList />
              </Suspense>
            }
          />

          {/* <Route
            path={clientRoutes?.createAgencyAgentEmployeeList}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyCreateAgentEmployee />
              </Suspense>
            }
          /> */}

          <Route
            path={clientRoutes?.agencyAgentViewEmployee}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyAgentEmployeeDetails />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.PropertyPost}
            element={
              <Suspense fallback={<div></div>}>
                <PropertyPost />
              </Suspense>
            }
          />

          {/* new property post design routes */}

          {/* <Route
            path={clientRoutes?.NewPropertyPost}
            element={
              <Suspense fallback={<div></div>}>
                <NewPropertyPost />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.NewPropertyPostList}
            element={
              <Suspense fallback={<div></div>}>
                <NewPropertyPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.NewPropertyPostView}
            element={
              <Suspense fallback={<div></div>}>
                <NewPropertyPostView />
              </Suspense>
            }
          /> */}

          <Route
            path={clientRoutes?.allActivities}
            element={
              <Suspense fallback={<div></div>}>
                <WorkspaceAllActivities />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.agentPropertyPost}
            element={
              <Suspense fallback={<div></div>}>
                <AgentPropertyPost />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.scheduleOpenHome}
            element={
              <Suspense fallback={<div></div>}>
                <ScheduleOpenHome />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.WorkspaceCreatereviewPost}
            element={
              <Suspense fallback={<div></div>}>
                <CreateReviewPost />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.OnBoardingPost}
            element={
              <Suspense fallback={<div></div>}>
                <CreateOnBoardingPost />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AnnouncementPost}
            element={
              <Suspense fallback={<div></div>}>
                <CreateAnnouncementPost />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyAnnouncementPost}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyCreateAnnouncementPost />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.FestivalPost}
            element={
              <Suspense fallback={<div></div>}>
                <CreateFestivalPost />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyFestivalPost}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyCreateFestivalPost />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyOnboardingPost}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyCreateOnBoardingPost />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.WorkspaceSchedule}
            element={
              <Suspense fallback={<div></div>}>
                <WorkspaceSchedule />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.WorkspaceScheduleList}
            element={
              <Suspense fallback={<div></div>}>
                <ScheduleAuctionList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.PropertyCheckoutSuccess}
            element={
              <Suspense fallback={<div></div>}>
                <PropertyPlanSuccess />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgentMarketingCheckoutSuccess}
            element={
              <Suspense fallback={<div></div>}>
                <AgentPlanSuccess />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.PropertyPostViaCheckoutSuccess}
            element={
              <Suspense fallback={<div></div>}>
                <PropertyPostViaPlanSuccess />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgentPropertyPostViaCheckoutSuccess}
            element={
              <Suspense fallback={<div></div>}>
                <AgentPropertyPostViaPlanSuccess />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AuctionPostViaCheckoutSuccess}
            element={
              <Suspense fallback={<div></div>}>
                <AuctionPostViaPlanSuccess />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.OpenHomePostViaCheckoutSuccess}
            element={
              <Suspense fallback={<div></div>}>
                <OpenHomePostViaPlanSuccess />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.PropertyUpgradeCheckout}
            element={
              <Suspense fallback={<div></div>}>
                <PropertyPlanUpgrade />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.PropertyCheckoutFailed}
            element={
              <Suspense fallback={<div></div>}>
                <PropertyPlanFailed />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgentMarketingCheckoutFailed}
            element={
              <Suspense fallback={<div></div>}>
                <AgentPlanFailed />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.PropertyUpgradeCheckoutFailed}
            element={
              <Suspense fallback={<div></div>}>
                <PropertyPlanUpgradeFailed />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.ScheduleOpenHomeList}
            element={
              <Suspense fallback={<div></div>}>
                <ScheduleOpenHomeList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyOpenHomeList}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyScheduleOpenHomeList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyPropertyPost}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyPropertyPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.OnBoardingPostList}
            element={
              <Suspense fallback={<div></div>}>
                <OnBoardingPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyOnboardingPostList}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyOnboardingPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AnnouncementPostList}
            element={
              <Suspense fallback={<div></div>}>
                <AnnouncementPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyAnnouncementPostList}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyAnnouncementPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.FestivalPostList}
            element={
              <Suspense fallback={<div></div>}>
                <FestivalPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyFestivalPostList}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyFestivalPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyReviewPost}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyReviewPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyAuctionPost}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyScheduleAuctionList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.PropertyPostList}
            element={
              <Suspense fallback={<div></div>}>
                <PropertyPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgentPropertyPostList}
            element={
              <Suspense fallback={<div></div>}>
                <AgentPropertyPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.ReviewPostList}
            element={
              <Suspense fallback={<div></div>}>
                <ReviewPostList />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.WorkspaceSheduleConfigure}
            element={
              <Suspense fallback={<div></div>}>
                <WorkspaceSheduleConfigure />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.propertieView}
            element={
              <Suspense fallback={<div></div>}>
                <PropertieView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.scheduleOpenHomeView}
            element={
              <Suspense fallback={<div></div>}>
                <OpenHomeDetailView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyScheduleOpenHomeView}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyOpenHomeDetailView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyPropertyPostView}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyPropertyPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyScheduleAuctionView}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyScheduleAuctionView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyReviewPostView}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyReviewPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.PropertyPostView}
            element={
              <Suspense fallback={<div></div>}>
                <PropertyPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.PropertyPostJourneyView}
            element={
              <Suspense fallback={<div />}>
                <PropertyPostJourneyView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.CoAgentPropertyPostView}
            element={
              <Suspense fallback={<div></div>}>
                <CoAgentPropertyPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgentPropertyPostView}
            element={
              <Suspense fallback={<div></div>}>
                <AgentPropertyPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.CoAgentPropertyView}
            element={
              <Suspense fallback={<div></div>}>
                <CoAgentPropertyView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.ReviewPostView}
            element={
              <Suspense fallback={<div></div>}>
                <ReviewPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.OnboardingPostView}
            element={
              <Suspense fallback={<div></div>}>
                <OnboardingPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyOnboardingPostView}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyOnboardingPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AnnouncementPostView}
            element={
              <Suspense fallback={<div></div>}>
                <AnnouncementPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyAnnouncementPostView}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyAnnouncementPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.FestivalPostView}
            element={
              <Suspense fallback={<div></div>}>
                <FestivalPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.AgencyFestivalPostView}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyFestivalPostView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.ScheduleAuctionView}
            element={
              <Suspense fallback={<div></div>}>
                <ScheduleAuctionView />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.propertieEdit}
            element={
              <Suspense fallback={<div></div>}>
                <PropertieEdit />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.register}
            element={
              <Suspense fallback={<div></div>}>
                <Register />
              </Suspense>
            }
          />
          <Route
            path={clientRoutes.login}
            element={
              <Suspense fallback={<div></div>}>
                <SignIn />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentSignInVerifyEmail}
            element={
              <Suspense fallback={<div></div>}>
                <LoginVerifyEmail />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agencySignInVerifyEmail}
            element={
              <Suspense fallback={<div></div>}>
                <LoginVerifyEmail />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentVerifyEmail}
            element={
              <Suspense fallback={<div></div>}>
                <RegiterVerifyEmail />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentProfile}
            element={
              <Suspense fallback={<div></div>}>
                <AgentProfile />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentVerifyPhone}
            element={
              <Suspense fallback={<div></div>}>
                <AgentVerifyPhone />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentProfileAgencyInfo}
            element={
              <Suspense fallback={<div></div>}>
                <AgentAddAgencyInfo />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentProfileCreateAgencyProfile}
            element={
              <Suspense fallback={<div></div>}>
                <AgentProfileCreateAgencyProfile />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentCreateAgencyInfo}
            element={
              <Suspense fallback={<div></div>}>
                <AgentAgencyProfile />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentCreateAgencyVerifyPhone}
            element={
              <Suspense fallback={<div></div>}>
                <AgentAgencyProfileVerifyPhone />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentCreateAgencyLegalInfo}
            element={
              <Suspense fallback={<div></div>}>
                <AgentAgencyLegalInfo />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentCreateAgencyAgentInfo}
            element={
              <Suspense fallback={<div></div>}>
                <UpdateAgencyAgentInfo />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agentCreateAgencyBrandGrammar}
            element={
              <Suspense fallback={<div></div>}>
                <AgentAgencyBrandGrammar />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agencyVerifyEmail}
            element={
              <Suspense fallback={<div></div>}>
                <RegiterVerifyEmail />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agencyProfileAgencyInfo}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyProfile />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agencyProfileVerifyPhone}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyProfileVerifyPhone />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agencyProfileLegalInfo}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyLegalInfo />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agencyBrandGrammerInfo}
            element={
              <Suspense fallback={<div></div>}>
                <BrandGrammar />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes.agencyProfileAgentInfo}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyProfileAgentInfo />
              </Suspense>
            }
          />

          {/* <Route path={clientRoutes?.properties} element={<PropertieView />} /> */}
          <Route
            path={clientRoutes?.designTemplates}
            element={
              <Suspense fallback={<div></div>}>
                <DesignTemplateTypes />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.designTemplateTypes}
            element={
              <Suspense fallback={<div></div>}>
                <DesignTemplateSubTypes />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.masterdesignTemplateconfiguration}
            element={
              <Suspense fallback={<div></div>}>
                <MasterDesignConfigure />
              </Suspense>
            }
          />

          {/* <Route
            path={clientRoutes?.designAnnoucementTemplateTypes}
            element={
              <Suspense fallback={<div></div>}>
                <AnnouncementTypes />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.designFestivalTemplateTypes}
            element={
              <Suspense fallback={<div></div>}>
                <FestivalTypes />
              </Suspense>
            }
          /> */}

          <Route
            path={clientRoutes?.home}
            element={
              <Suspense fallback={<div></div>}>
                <HomeComponent />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.testComponent}
            element={
              <Suspense fallback={<div></div>}>
                <SavePaymentDetails />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.agencyApprovalWaiting}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyApprovalWaiting />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.adminApprovalWaiting}
            element={
              <Suspense fallback={<div></div>}>
                <AgencyAdminApprovalWaiting />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.agentAgencyApprovalWaiting}
            element={
              <Suspense fallback={<div></div>}>
                <AgentAgencyApprovalWaiting />
              </Suspense>
            }
          />

          <Route
            path={clientRoutes?.accountSettings}
            element={
              <Suspense fallback={<div></div>}>
                <AccountSettings />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Navigate to={clientRoutes.configurePropertyStages} replace />
              }
            />

            <Route
              path={clientRoutes.configurePropertyStages}
              element={<ConfigurePropertyStages />}
            />

            <Route
              path={clientRoutes.openHomePosts}
              element={<OpenHomePosts />}
            />

            <Route
              path={clientRoutes.auctionPosts}
              element={<AuctionPosts />}
            />

            <Route
              path={clientRoutes.recapPostSettings}
              element={<RecapPostSettings />}
            />
            <Route
              path={clientRoutes.otherPostSettings}
              element={
                <div style={{ padding: 20 }}>Other Post Settings Component</div>
              }
            />

            <Route
              path={clientRoutes.paymentMethods}
              element={<AgencyPaymentMethods />}
            />

            <Route
              path={clientRoutes.socialMediaAccounts}
              element={<SocialMediaAccounts />}
            />

             <Route
              path={clientRoutes.security}
              element={<Security />}
            />

            {/* <Route
              path={clientRoutes.manageSubscriptions}
              element={<ManageSubscription />}
            />

            <Route
              path={clientRoutes.propertyPlans}
              element={<ManagePropertySubscription />}
            />
            <Route
              path={clientRoutes.agentPlans}
              element={<ManageAgentSubscription />}
            /> */}
            {/* <Route path={clientRoutes.Ledger} element={<Ledger />} /> */}
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
